import { ref, type Ref } from 'vue';
import type { TableElement } from '../model/form.types';
import { TABLE_CONSTRAINTS } from '../lib/table-constants';
import { getResolvedColumnWidthsPx, isFractionalColumnWidths } from '../lib/table-column-utils';

function resolveVisualRow(
  row: number,
  rowSpan: number,
  mouseY: number,
  rowHeights: number[]
): number {
  let accumulatedHeight = 0;
  for (let i = 0; i < rowSpan; i++) {
    accumulatedHeight += rowHeights[row + i] || TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT;
    if (mouseY < accumulatedHeight) return row + i;
  }
  return row + rowSpan - 1;
}

function resolveVisualCol(
  col: number,
  colSpan: number,
  mouseX: number,
  resolvedWidths: number[]
): number {
  let accumulatedWidth = 0;
  for (let i = 0; i < colSpan; i++) {
    accumulatedWidth += resolvedWidths[col + i];
    if (mouseX < accumulatedWidth) return col + i;
  }
  return col + colSpan - 1;
}

export function useTableHover(
  tableElement: Ref<TableElement>,
  actualRowHeights: Ref<number[]>,
  gridRef: Ref<HTMLElement | null>
) {
  const hoveredVisualRow = ref<number | null>(null);
  const hoveredVisualCol = ref<number | null>(null);

  const handleCellHover = (row: number | null, col: number | null) => {
    if (row === null || col === null) {
      hoveredVisualRow.value = null;
      hoveredVisualCol.value = null;
    } else {
      hoveredVisualRow.value = row;
      hoveredVisualCol.value = col;
    }
  };

  const handleCellMouseMove = (row: number, col: number, event: MouseEvent) => {
    const cell = tableElement.value.properties.cells[row]?.[col];
    if (!cell) return;

    const rowSpan = cell.rowSpan || 1;
    const colSpan = cell.colSpan || 1;

    if (rowSpan === 1 && colSpan === 1) {
      hoveredVisualRow.value = row;
      hoveredVisualCol.value = col;
      return;
    }

    const target = event.currentTarget as HTMLElement;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const mouseY = event.clientY - rect.top;
    const mouseX = event.clientX - rect.left;

    hoveredVisualRow.value =
      rowSpan > 1 ? resolveVisualRow(row, rowSpan, mouseY, actualRowHeights.value) : row;

    if (colSpan <= 1) {
      hoveredVisualCol.value = col;
      return;
    }

    const columnWidths = tableElement.value.properties.columnWidths;
    const columns = tableElement.value.properties.columns;
    const hasPixelWidths = columnWidths?.length === columns && !isFractionalColumnWidths(columnWidths);

    if (!hasPixelWidths && !gridRef.value) {
      hoveredVisualCol.value = col;
      return;
    }

    const gridWidth = gridRef.value?.offsetWidth ?? 0;
    const resolvedWidths = getResolvedColumnWidthsPx(columnWidths, columns, gridWidth);
    hoveredVisualCol.value = resolveVisualCol(col, colSpan, mouseX, resolvedWidths);
  };

  const isRowButtonVisible = (rowIndex: number): boolean => {
    return hoveredVisualRow.value === rowIndex;
  };

  const isColButtonVisible = (colIndex: number): boolean => {
    return hoveredVisualCol.value === colIndex;
  };

  return {
    hoveredVisualRow,
    hoveredVisualCol,
    handleCellHover,
    handleCellMouseMove,
    isRowButtonVisible,
    isColButtonVisible,
  };
}
